"use client";

import { clsx } from "@trydig/ui";
import { type ComponentProps, type MouseEvent, useRef, useState } from "react";
import { SoundIcon } from "@/components/icons/Sound";
import styles from "./Asset.module.css";

interface VideoProps extends ComponentProps<"video"> {
	src: string;
	soundControl?: boolean;
	soundControlLight?: boolean;
	wrapClassName?: string;
}

export const Video = ({
	src,
	soundControl,
	soundControlLight,
	wrapClassName,
	className,
	autoPlay = true,
	loop = true,
	muted = true,
	controls,
	...rest
}: VideoProps) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const [isMuted, setIsMuted] = useState(true);

	const toggleSound = (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		const video = videoRef.current;
		if (!video) return;
		const next = !video.muted;
		video.muted = next;
		if (!next) {
			video.play().catch(() => {});
		}
		setIsMuted(next);
	};

	return (
		<div className={clsx(styles.videoWrap, wrapClassName)}>
			<video
				ref={videoRef}
				className={className}
				playsInline
				autoPlay={autoPlay}
				loop={loop}
				muted={muted}
				controls={controls}
				{...rest}
			>
				<source src={src} />
			</video>
			{soundControl && (
				<button
					type="button"
					className={styles.playToggle}
					data-light={soundControlLight}
					onClick={toggleSound}
					aria-label={isMuted ? "Unmute video" : "Mute video"}
					aria-pressed={!isMuted}
				>
					<SoundIcon muted={isMuted} />
				</button>
			)}
		</div>
	);
};
